import React, { useState, useEffect } from 'react';
import {
  Building2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  User,
  Calendar,
  Eye,
  Check,
  X,
  Loader
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';
import { getTenantFromUrl } from '../utils/enhancedTenantUtils';
import toast from 'react-hot-toast';
import api from '../api';

interface TenantRequest {
  id: number;
  organization_name: string;
  preferred_subdomain: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  company_size: string;
  industry?: string;
  requested_plan: string;
  estimated_users: number;
  billing_email?: string;
  payment_method?: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'provisioned';
  rejection_reason?: string;
  internal_notes?: string;
  use_case?: string;
  referral_source?: string;
  created_at: string;
  reviewed_at?: string;
  approved_at?: string;
  tenant_id?: number;
}

const TenantRequests: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<TenantRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<TenantRequest | null>(null);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [approveForm, setApproveForm] = useState({
    final_subdomain: '',
    final_plan: '',
    max_users: 0,
    max_projects: 0,
    storage_limit_gb: 0,
    trial_days: 14
  });
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // Security check
  if (!user || user.role !== 'SUPER_ADMIN') {
    return <AccessDenied requiredRole="SUPER_ADMIN" message="Tenant request management is restricted to Super Admin users only." />;
  }


  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/tenant-signup/requests', {
        params: { status: statusFilter }
      });
      setRequests(response.data.requests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to fetch tenant requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const handleReview = async (request: TenantRequest) => {
    try {
      await api.put(`/api/tenant-signup/requests/${request.id}/review`, {
        internal_notes: 'Under review'
      });
      toast.success('Request marked as under review');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      const response = await api.post(`/api/tenant-signup/requests/${selectedRequest.id}/approve`, {
        final_subdomain: approveForm.final_subdomain || selectedRequest.preferred_subdomain,
        final_plan: approveForm.final_plan || selectedRequest.requested_plan,
        max_users: approveForm.max_users || undefined,
        max_projects: approveForm.max_projects || undefined,
        storage_limit_gb: approveForm.storage_limit_gb || undefined,
        trial_days: approveForm.trial_days
      });
      
      toast.success('Tenant successfully provisioned!');
      setShowApproveModal(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to provision tenant');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    
    setProcessing(true);
    try {
      await api.post(`/api/tenant-signup/requests/${selectedRequest.id}/reject`, {
        rejection_reason: rejectReason
      });
      
      toast.success('Request rejected');
      setShowRejectModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      fetchRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'under_review': return <Eye className="w-4 h-4 text-blue-500" />;
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'provisioned': return <CheckCircle className="w-4 h-4 text-green-700" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'under_review': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'provisioned': return 'bg-green-200 text-green-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Building2 className="w-8 h-8 mr-3 text-electric-blue" />
              Tenant Requests
            </h1>
            <p className="text-gray-600 mt-1">Review and approve tenant signup requests</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex gap-2">
            {['pending', 'under_review', 'approved', 'rejected', 'provisioned'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2 rounded-lg capitalize ${
                  statusFilter === status
                    ? 'bg-electric-blue text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      <Loader className="w-8 h-8 animate-spin mx-auto text-electric-blue" />
                    </td>
                  </tr>
                ) : requests.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No requests found
                    </td>
                  </tr>
                ) : (
                  requests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {request.organization_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            @{request.preferred_subdomain}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.company_size} employees
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-900 flex items-center">
                            <User className="w-4 h-4 mr-1" />
                            {request.contact_name}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {request.contact_email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 capitalize">
                            {request.requested_plan}
                          </div>
                          <div className="text-sm text-gray-500">
                            {request.estimated_users} users
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(request.status)}
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(request.status)}`}>
                            {request.status.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(request.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {request.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleReview(request)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Mark as under review"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setApproveForm({
                                    final_subdomain: request.preferred_subdomain,
                                    final_plan: request.requested_plan,
                                    max_users: 0,
                                    max_projects: 0,
                                    storage_limit_gb: 0,
                                    trial_days: 14
                                  });
                                  setShowApproveModal(true);
                                }}
                                className="text-green-600 hover:text-green-900"
                                title="Approve request"
                              >
                                <Check className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setShowRejectModal(true);
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Reject request"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </>
                          )}
                          {request.status === 'under_review' && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setApproveForm({
                                    final_subdomain: request.preferred_subdomain,
                                    final_plan: request.requested_plan,
                                    max_users: 0,
                                    max_projects: 0,
                                    storage_limit_gb: 0,
                                    trial_days: 14
                                  });
                                  setShowApproveModal(true);
                                }}
                                className="text-green-600 hover:text-green-900"
                                title="Approve request"
                              >
                                <Check className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedRequest(request);
                                  setShowRejectModal(true);
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Reject request"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Approve Tenant Request
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization
                  </label>
                  <p className="text-gray-900">{selectedRequest.organization_name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subdomain
                  </label>
                  <input
                    type="text"
                    value={approveForm.final_subdomain}
                    onChange={(e) => setApproveForm({ ...approveForm, final_subdomain: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan
                  </label>
                  <select
                    value={approveForm.final_plan}
                    onChange={(e) => setApproveForm({ ...approveForm, final_plan: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="basic">Basic</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trial Days
                  </label>
                  <input
                    type="number"
                    value={approveForm.trial_days}
                    onChange={(e) => setApproveForm({ ...approveForm, trial_days: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {processing ? 'Provisioning...' : 'Approve & Provision'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Reject Tenant Request
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization
                  </label>
                  <p className="text-gray-900">{selectedRequest.organization_name}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejection Reason
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Provide a reason for rejection..."
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing || !rejectReason}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {processing ? 'Rejecting...' : 'Reject Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantRequests;